import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { PaymentMethod, PaymentMethods, PaymentMethodsType } from '@cargos/sprintpay-models';
import { Subject, takeUntil } from 'rxjs';
import { RnplSloganComponent } from 'src/app/shared/components/rnpl-slogan/rnpl-slogan.component';
import { PaymentMethodsService } from 'src/app/shared/services';
import { FeatureRestrictionsService } from 'src/app/shared/services/features/feature-restrictions.service';
import { PaymentMethodsHandlerService } from '../../shared/services/payment-methods-handler.service';
import { LogoPaymentMethodComponent } from '../logo-payment-method/logo-payment-method.component';

@Component({
    standalone: true,
    selector: 'app-sprintpay-flex',
    imports: [
        CommonModule,
        LogoPaymentMethodComponent,
        RouterModule,
        MatListModule,
        MatTooltipModule,
        MatIconModule,
        RnplSloganComponent,
    ],
    templateUrl: './sprintpay-flex.component.html',
    styleUrls: ['./sprintpay-flex.component.scss'],
    providers: [CurrencyPipe],
})
export class SprintpayFlexComponent implements OnInit, OnDestroy {
    public sprintpayFlexCredit?: PaymentMethod;
    public PaymentMethodsEnum = PaymentMethods;
    public paymentMethodSelected: PaymentMethodsType | null = null;
    private unsubscribe$: Subject<void> = new Subject<void>();
    public isEnoughCredit?: boolean;

    /**
     * This variable does not indicate whether the cart is above the threshold.
     * Instead, it serves as a configuration to determine the selected payment method based on the section.
     */
    @Input() aboveThreshold: boolean = false;
    @Output() eventOnSelectSprintpayFlex: EventEmitter<string> = new EventEmitter<string>();

    constructor(
        private paymentMethodsService: PaymentMethodsService,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService,
        private featureRestrictionsService: FeatureRestrictionsService,
        private currencyPipe: CurrencyPipe
    ) {}

    ngOnInit(): void {
        this.subscribeFinanceCredit();
        this.subscribePaymentMethodSelected();
        this.subscribeIsAllowedToPayWithCredit();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    subscribeFinanceCredit(): void {
        this.paymentMethodsService
            .getPaymentMethodsByType$(PaymentMethods.SPRINTPAY_FLEX)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (financeCredit: PaymentMethod | undefined) => {
                    this.sprintpayFlexCredit = financeCredit;
                },
            });
    }

    selectPaymentMethod(): void {
        this.eventOnSelectSprintpayFlex.emit(PaymentMethods.SPRINTPAY_FLEX);
    }

    subscribePaymentMethodSelected(): void {
        this.paymentMethodsHandlerService
            .getPaymentMethodSelected$(this.aboveThreshold)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((paymentMethodSelected) => {
                this.paymentMethodSelected = paymentMethodSelected?.method || null;
            });
    }

    subscribeIsAllowedToPayWithCredit(): void {
        this.featureRestrictionsService
            .isAllowedToPayWithCredit$(PaymentMethods.SPRINTPAY_FLEX, this.aboveThreshold)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((isAllowedToPayWithCredit) => {
                this.isEnoughCredit = isAllowedToPayWithCredit.isEnoughCredit;
            });
    }

    get insufficientCreditWarning(): string {
        const formatedCredit =
            this.currencyPipe.transform(this.sprintpayFlexCredit?.balance?.avaliableCredit) ?? '$0.00';
        return `Your Credit Available is ${formatedCredit}, so this transaction cannot be processed. Please select a different payment method to proceed.`;
    }
}
